import * as React from 'react';

import Button from 'src/components/common/Button';
import AddressDescription from 'src/components/ShippingToChange/components/AddressDescription/AddressDescription';
import AddressLoading from 'src/components/ShippingToChange/components/AddressLoading';
import Input from 'src/components/common/Input';
import AccountInfoHeader from '../AccountInfoHeader';
import AccountInfoFooter from '../AccountInfoFooter';

import AddressModel from 'src/model/address.model';
import UserModel from 'src/model/user.model';
import ShipAddressModel from 'src/model/shipAddress.model';
import { IAddressValidation } from 'src/model/modules/accountview.model';
import env from 'src/shared/env';

import './AccountShip.scss';

interface IAccountShipProps {
  loading: boolean;
  error: boolean;
  addresses: AddressModel[];
  userData: UserModel;
  addressRequest: {
    address: ShipAddressModel;
    addressValidation: IAddressValidation;
    succed: boolean;
    failed: boolean;
  };
  loadAddresses(): void;
  onSearchTextChange(text: string): void;
  onSearch(): void;
  onAddAddress(params: ShipAddressModel): void;
  onChangeAddress(params: ShipAddressModel): void;
  onRemoveAddress(params: { addressId: number; customerNumber: number }): void;
  onSetAddress(params?: ShipAddressModel): void;
  onClearAddressValidation(): void;
  t?: any;
}

interface IAccountShipState {
  searchText: string;
  showForm: boolean;
  addAddress: boolean;
}

export class AccountShip extends React.Component<
  IAccountShipProps,
  IAccountShipState
> {
  public componentDidMount() {
    this.props.loadAddresses();
  }

  constructor(props: any) {
    super(props);

    this.state = {
      searchText: '',
      showForm: false,
      addAddress: false
    };
  }

  private handleCustomerNameChange = (customerName: string) => {
    this.props.onSetAddress({
      ...this.props.addressRequest.address,
      customerName
    });
  };

  private handleAddressChange = (address: string) => {
    this.props.onSetAddress({
      ...this.props.addressRequest.address,
      address
    });
  };

  private handleAddressOptionalChange = (addressOptional: string) => {
    this.props.onSetAddress({
      ...this.props.addressRequest.address,
      address2: addressOptional
    });
  };

  private handleCountryChange = (country: string) => {
    this.props.onSetAddress({
      ...this.props.addressRequest.address,
      country
    });
  };

  private handleStateChange = (state: string) => {
    this.props.onSetAddress({ ...this.props.addressRequest.address, state });
  };

  private handleCityChange = (city: string) => {
    this.props.onSetAddress({ ...this.props.addressRequest.address, city });
  };

  private handleZipCodeChange = (zipCode: string) => {
    this.props.onSetAddress({
      ...this.props.addressRequest.address,
      zipCode
    });
  };

  private handleSearchTextChange = (searchText: string) => {
    this.setState({ searchText });
    this.handleSearchAddress(searchText);
  };

  private handleRequestAdd = () => {
    this.props.onAddAddress({
      address: this.props.addressRequest.address.address,
      address2: this.props.addressRequest.address.address2,
      city: this.props.addressRequest.address.city,
      country: this.props.addressRequest.address.country,
      customerName: this.props.addressRequest.address.customerName,
      customerNumber: Number(this.props.userData.accountId),
      state: this.props.addressRequest.address.state,
      zipCode: this.props.addressRequest.address.zipCode
    });
  };

  private handleRequestChange = () => {
    this.props.onChangeAddress({
      address: this.props.addressRequest.address.address,
      address2: this.props.addressRequest.address.address2,
      addressId: this.props.addressRequest.address.addressId,
      city: this.props.addressRequest.address.city,
      country: this.props.addressRequest.address.country,
      customerName: this.props.addressRequest.address.customerName,
      customerNumber: Number(this.props.userData.accountId),
      state: this.props.addressRequest.address.state,
      zipCode: this.props.addressRequest.address.zipCode
    });
  };

  private handleRequestRemoval = () => {
    this.props.onRemoveAddress({
      addressId: this.props.addressRequest.address.addressId,
      customerNumber: Number(this.props.userData.accountId)
    });
  };

  private onSelectAddress = (address: AddressModel) => {
    this.props.onClearAddressValidation();
    this.props.onSetAddress({
      customerName: '',
      addressId: address.number,
      address: `${address.addressLine1} ${address.addressLine2} ${address.addressLine3}`,
      address2: '',
      country: address.country,
      state: address.stateCode,
      customerNumber: Number(this.props.userData.accountId),
      city: address.addressLine4,
      zipCode: address.postalCode
    });
    this.setState({
      showForm: true,
      addAddress: false
    });
  };

  private handleClickAddAddress = () => {
    this.props.onClearAddressValidation();
    this.props.onSetAddress();
    this.setState({
      showForm: true,
      addAddress: true
    });
  };

  private handleCancelAdd = () => {
    this.setState({
      showForm: false,
      addAddress: false
    });
  };

  private handleSearchAddress = (searchText: string) => {
    let results = this.props.addresses;
    if (searchText) {
      results = this.props.addresses.filter(address => {
        return (
          `${
            address.addressLine1
              ? `${address.addressLine1.toLocaleLowerCase()}, `
              : ''
          }${
            address.addressLine2
              ? `${address.addressLine2.toLocaleLowerCase()}, `
              : ''
          }${
            address.addressLine3
              ? `${address.addressLine3.toLocaleLowerCase()}, `
              : ''
          }${
            address.addressLine4
              ? `${address.addressLine4.toLocaleLowerCase()}, `
              : ''
          }${address.stateCode ? `${address.stateCode} ` : ''}${
            address.postalCode ? `${address.postalCode}` : ''
          }`
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          address.name.toLowerCase().includes(searchText.toLowerCase())
        );
      });
    }
    return results;
  };

  private renderForm = () => {
    const { t } = this.props;
    return (
      <div className="rp-accountship--form">
        <div className="rp-accountship--form-title">
          {this.state.addAddress
            ? `${t('accountShippingAddress.newAddressRequestFormTitle')}`
            : `${t('accountShippingAddress.editRequestFormTitle')}`}
        </div>
        <div className="rp-accountship--form-form">
          <Input
            label={`*${t('accountShippingAddress.customerName')}:`}
            value={this.props.addressRequest.address.customerName}
            onChange={this.handleCustomerNameChange}
            invalid={
              this.props.addressRequest.addressValidation.customerName.invalid
            }
            invalidMessage={
              this.props.addressRequest.addressValidation.customerName.message
            }
          />
          <Input
            label={`*${t('accountShippingAddress.country')}:`}
            value={this.props.addressRequest.address.country}
            onChange={this.handleCountryChange}
            invalid={
              this.props.addressRequest.addressValidation.country.invalid
            }
            invalidMessage={
              this.props.addressRequest.addressValidation.country.message
            }
          />
          <Input
            label={`*${t('accountShippingAddress.address')}:`}
            placeholder={`${t('accountShippingAddress.addressPlaceholder')}`}
            value={this.props.addressRequest.address.address}
            onChange={this.handleAddressChange}
            invalid={
              this.props.addressRequest.addressValidation.address.invalid
            }
            invalidMessage={
              this.props.addressRequest.addressValidation.address.message
            }
          />
          <Input
            value={this.props.addressRequest.address.address2}
            placeholder={`${t('accountShippingAddress.suiteTxt')}`}
            onChange={this.handleAddressOptionalChange}
          />
          <Input
            label={`*${t('accountShippingAddress.stateOrProvince')}:`}
            value={this.props.addressRequest.address.state}
            onChange={this.handleStateChange}
            invalid={this.props.addressRequest.addressValidation.state.invalid}
            invalidMessage={
              this.props.addressRequest.addressValidation.state.message
            }
          />
          <Input
            label={`*${t('accountShippingAddress.city')}:`}
            value={this.props.addressRequest.address.city}
            onChange={this.handleCityChange}
            invalid={this.props.addressRequest.addressValidation.city.invalid}
            invalidMessage={
              this.props.addressRequest.addressValidation.city.message
            }
          />
          <Input
            label={
              env.REACT_APP_BASE_ENVIRONMENT === 'CA'
                ? `*${t('accountShippingAddress.postalCode')}:`
                : `*${t('accountShippingAddress.zipOrPostal')}:`
            }
            value={this.props.addressRequest.address.zipCode}
            onChange={this.handleZipCodeChange}
            invalid={
              this.props.addressRequest.addressValidation.zipCode.invalid
            }
            invalidMessage={
              this.props.addressRequest.addressValidation.zipCode.message
            }
          />
        </div>
        <div className="rp-accountship--form-buttons">
          {this.state.addAddress ? (
            <React.Fragment>
              <button
                className="secondary-button"
                onClick={e => this.handleCancelAdd()}
              >
                {t('common.cancelAction')}
              </button>
              <button
                className="primary-button"
                onClick={e => this.handleRequestAdd()}
              >
                {t('accountShippingAddress.requestNewAddressAction')}
              </button>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <button
                className="secondary-button"
                onClick={e => this.handleRequestRemoval()}
              >
                {t('accountShippingAddress.requestRemovalAction')}
              </button>
              <button
                className="primary-button"
                onClick={e => this.handleRequestChange()}
              >
                {t('accountInfo.requestChange')}
              </button>
            </React.Fragment>
          )}
        </div>
      </div>
    );
  };

  public render() {
    const props = this.props;
    const { t } = this.props;
    return (
      <div className="rp-accountship">
        <AccountInfoHeader />
        <div className="row">
          <div className="col-12 col-md-8 rp-accountship-left-column">
            <div className="rp-accountship--content">
              <div className="row">
                <div className="col-12 col-md-6">
                  <h1 className="rp-accountship--title">
                    {t('accountShippingAddress.shippingAddressHeaderTxt')}
                  </h1>
                </div>
                <div className="col-12 col-md-6 rp-accountship--newbtn-container">
                  <Button
                    label={t('accountShippingAddress.addnewRequestAction')}
                    textTransform="uppercase"
                    buttonClass="primary-button rp-accountship--newbtn"
                    height="40px"
                    action={this.handleClickAddAddress}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 rp-accountship--searchbbar-container">
                  <div className="rp-accountship--searchbbar">
                    <input
                      className="rp-accountship--searchbbar-input"
                      placeholder={t(
                        'accountShippingAddress.searchAddressPlaceHolder'
                      )}
                      name="search-address"
                      onChange={event =>
                        this.handleSearchTextChange(event.target.value)
                      }
                      autoComplete="search-shipping-address-rp"
                    />
                    <button
                      className="primary-button rp-accountship--searchbbar-button"
                      onClick={e =>
                        this.handleSearchAddress(this.state.searchText)
                      }
                    >
                      <span className="rpds rpds-search" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12 rp-accountship--addresses">
                  {props.loading ? (
                    <React.Fragment>
                      <AddressLoading key={1} />
                      <AddressLoading key={2} />
                      <AddressLoading key={3} />
                    </React.Fragment>
                  ) : (
                    this.handleSearchAddress(
                      this.state.searchText
                    ).map(address => (
                      <AddressDescription
                        key={address.number}
                        address={address}
                        handleClicDeliverAddress={this.onSelectAddress}
                        buttonText={t('common.editActionTxt')}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4 rp-accountship-right-column">
            {this.state.showForm ? (
              this.renderForm()
            ) : (
              <div className="rp-accountship--aside">
                <h1>{t('accountShippingAddress.selectAddressToEdit')}</h1>
                <p>{t('accountShippingAddress.selectAddressHint')}</p>
              </div>
            )}
          </div>
        </div>
        <AccountInfoFooter />
      </div>
    );
  }
}
